import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import U2000ActionBtn from "../../../../u2000Core/components/buttons/U2000ActionBtn";
import U2000ActionDropdownBtn from "../../../../u2000Core/components/buttons/U2000ActionDropdownBtn";
import type { U2000ActionDropdownItem } from "../../../../u2000Core/components/buttons/U2000ActionDropdownBtn";
import U2000ResponsiveToolbar from "../../../../u2000Core/components/toolbar/U2000ResponsiveToolbar";
import { FaDownload, FaKey, FaPlus, FaRegFile, FaTrashAlt } from "react-icons/fa";
import { useU2000AuthContext } from "../../../../u2000Core/context/auth/U2000UseAuthContext";
import { U2000FormStateEnumState } from "../../../../u2000Core/reducer/formReducer/U2000FormState";
import { NS, U3141ROLES } from "../../../U3141";
import { useU3141Context } from "../../../U3141Context";
import type { U2000FormStateHandlersReturn } from "../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import type { U3141MasterLotChain } from "../../../U3141Service";
type U3141MasterLotChainToolbarHandlers = U2000FormStateHandlersReturn<U3141MasterLotChain> & {
  addNewMasterLotChain: () => void;
  modifyMasterLotChain: () => void;
  deleteMasterLotChain: () => void;
  mintPlanUploadToken: (masterLotChainId: string) => Promise<void>;
  downloadSiteAppliSFoncier: () => Promise<void>;
};

interface U3141MasterLotChainFormBtnsComponentProps {
  formStateHandlers: U3141MasterLotChainToolbarHandlers;
}

export const U3141MasterLotChainFormToolBar: React.FC<U3141MasterLotChainFormBtnsComponentProps> = ({ formStateHandlers }) => {
  const formState = formStateHandlers.getFormState();
  const currentData = formStateHandlers.getData();
  const currentId = currentData?.id;

  const { hasRole } = useU2000AuthContext();
  const { unitInitLU } = useU3141Context();
  const { t } = useTranslation();
  const siteAppliVersionUrl = unitInitLU.siteAppliSFoncierVersionUrl;

  const plansItems = useMemo((): U2000ActionDropdownItem[] => {
    const items: U2000ActionDropdownItem[] = [
      {
        key: "mintPlanUploadToken",
        label: t("btnMintPlanUploadToken", { ns: NS }),
        icon: FaKey,
        handleClick: () => {
          if (currentId) void formStateHandlers.mintPlanUploadToken(currentId);
        },
        disabled: formState === U2000FormStateEnumState.EDIT || !currentId,
      },
    ];
    if (siteAppliVersionUrl) {
      items.push({
        key: "downloadSiteAppliSFoncier",
        label: t("btnDownloadSiteAppliSFoncier", { ns: NS }),
        icon: FaDownload,
        handleClick: () => {
          void formStateHandlers.downloadSiteAppliSFoncier();
        },
        disabled: formState === U2000FormStateEnumState.EDIT || !siteAppliVersionUrl,
      });
    }
    return items;
  }, [currentId, formState, formStateHandlers, siteAppliVersionUrl, t]);

  const actions = [
    {
      key: "add",
      label: t("btnAdd", { ns: NS }),
      hasRole: hasRole(U3141ROLES.ADD),
      handleClick: () => {
        formStateHandlers.addNewMasterLotChain();
      },
      disabled: formState === U2000FormStateEnumState.EDIT,
      icon: FaPlus,
      Component: U2000ActionBtn,
    },
    {
      key: "modify",
      label: t("btnModify", { ns: NS }),
      hasRole: hasRole(U3141ROLES.EDIT),
      handleClick: () => {
        formStateHandlers.modifyMasterLotChain();
      },
      disabled: formState === U2000FormStateEnumState.EDIT,
      icon: FaRegFile,
      Component: U2000ActionBtn,
    },
    {
      key: "delete",
      label: t("btnDelete", { ns: NS }),
      hasRole: hasRole(U3141ROLES.DELETE),
      handleClick: () => {
        formStateHandlers.deleteMasterLotChain();
      },
      disabled: formState === U2000FormStateEnumState.EDIT,
      icon: FaTrashAlt,
      Component: U2000ActionBtn,
    },
    {
      key: "plans",
      label: t("btnToolbarPlans", { ns: NS }),
      hasRole: hasRole(U3141ROLES.READ),
      handleClick: () => {},
      icon: FaKey,
      Component: U2000ActionDropdownBtn,
      menuAlign: "left" as const,
      items: plansItems,
    },
  ];

  return <U2000ResponsiveToolbar actions={actions} />;
};
